import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { query } from '@warp-drive/utilities/json-api';
import { PhBuildings, PhGraduationCap, PhUserCircle, PhUsersThree } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import SectionPanel from 'tio-ui/components/section-panel';
import { getFieldValue } from '../../../utils/tas/program-builder/sections';
import type { TOC } from '@ember/component/template-only';
import type IntlService from 'ember-intl/services/intl';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { TasProgramTemplateInstitutionExtension as TasProgramTemplateInstitutionModel } from 'tio-data/data/resources/tas-program-template-institution.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];

/** SNAKE_CAPS enum name → Title Case (CHILD → "Child"), matching the builder. */
function humanizeEnumName(name: string): string {
  return name
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface SchoolRow {
  name: string;
  address: string;
  accredited: boolean;
  isFeatured: boolean;
  isPreferred: boolean;
}

// Rows always visible before the rest collapse behind the disclosure.
const SCHOOL_ROWS_ALWAYS_VISIBLE = 8;

interface SchoolRowItemSignature {
  Args: {
    row: SchoolRow;
  };
}

// One school in the managed-schools list — shared by the always-visible rows
// and the rows behind the "Show all" disclosure.
const SchoolRowItem: TOC<SchoolRowItemSignature> = <template>
  <div
    class="tio-row-between border-b px-3 py-2 tio-border-subtle last:border-0"
    data-test-readout-school-row={{@row.name}}
  >
    <div class="tio-fill-min">
      <div class="truncate tio-text-sm-medium">{{@row.name}}</div>
      {{#if @row.address}}
        <div class="truncate text-xs tio-fg-muted">{{@row.address}}</div>
      {{/if}}
    </div>
    <div class="tio-row-extra-tight shrink-0">
      {{#if @row.isFeatured}}
        <Chip @size="sm" @intent="success" @appearance="faded">{{t
            "program_builder.readout.school_featured_chip"
          }}</Chip>
      {{/if}}
      {{#if @row.isPreferred}}
        <Chip @size="sm" @intent="primary" @appearance="faded">{{t
            "program_builder.readout.school_preferred_chip"
          }}</Chip>
      {{/if}}
      <Chip @size="sm" @intent={{if @row.accredited "primary" "warning"}} @appearance="faded">
        {{#if @row.accredited}}
          {{t "program_builder.readout.school_accredited_chip"}}
        {{else}}
          {{t "program_builder.readout.school_unaccredited_chip"}}
        {{/if}}
      </Chip>
    </div>
  </div>
</template>;

interface ReadoutEligibilityPanelSignature {
  Args: {
    /** The template — its id keys the managed-schools (`tas-program-template-institution`) lookup. */
    model: TASProgramTemplateModel;
    fields: FieldsSignature;
    /** Reveal internal FIELD_NAMES badges (admin builder's devops toggle). */
    showFieldNames?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Read-only Eligibility & Institutions: the waiting period, the institution
 * flags with the program's managed-schools list (loaded from the same
 * `tas-program-template-institution` records the builder manages — shown as a
 * graceful note when that lookup isn't available), and the dependent program
 * with its per-relationship age limits.
 */
export default class ReadoutEligibilityPanel extends Component<ReadoutEligibilityPanelSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  private bool(name: string): boolean {
    return Boolean(getFieldValue(this.args.fields, name));
  }

  private str(name: string): string {
    return String(getFieldValue(this.args.fields, name) ?? '').trim();
  }

  get waitingPeriodValue(): string {
    const days = this.str('ELIGIBILITY_WAITING_PERIOD');
    return days ? this.intl.t('program_builder.summary.value.days', { count: Number(days) }) : '—';
  }

  /** Localized Yes/No for a boolean setting — a real answer, never an em dash. */
  private yesNo(on: boolean): string {
    return this.intl.t(
      on ? 'program_builder.summary.value.yes' : 'program_builder.summary.value.no'
    );
  }

  get listExclusive(): boolean {
    return this.bool('INSTITUTION_LIST_IS_EXCLUSIVE');
  }

  get allowUnaccreditedValue(): string {
    return this.yesNo(this.bool('ALLOW_UNACCREDITED_INSTITUTIONS'));
  }

  get listExclusiveValue(): string {
    return this.yesNo(this.listExclusive);
  }

  get dependentProgram(): boolean {
    return this.bool('DEPENDENT_PROGRAM');
  }

  get dependentProgramValue(): string {
    return this.yesNo(this.dependentProgram);
  }

  get schoolsHintKey(): string {
    return this.listExclusive
      ? 'program_builder.readout.schools_hint_exclusive'
      : 'program_builder.readout.schools_hint_open';
  }

  /** Same query the builder's Manage Schools screen runs; 'error' marks the list
   * unavailable (endpoint or permission) so the flags still render on their own. */
  private schoolLinksData = trackedFunction(this, async (): Promise<SchoolRow[] | 'error'> => {
    const templateId = this.args.model.id;
    if (!templateId) return [];
    try {
      const { content } = await this.store.request(
        query<TasProgramTemplateInstitutionModel>('tas-program-template-institution', {
          'filter[tas-program-template]': templateId as string,
          include: 'accredited-institution,unaccredited-institution',
          // The API's maximum page size — enough for any real program's list.
          'page[limit]': 400,
        })
      );
      return ((content.data ?? []) as TasProgramTemplateInstitutionModel[]).map((link) => ({
        name:
          link.accreditedInstitution?.locationName ||
          link.unaccreditedInstitution?.locationName ||
          '',
        address: link.accreditedInstitution?.address ?? link.unaccreditedInstitution?.address ?? '',
        accredited: Boolean(link.accreditedInstitution),
        isFeatured: Boolean(link.isFeatured),
        isPreferred: Boolean(link.isPreferred),
      }));
    } catch {
      return 'error';
    }
  });

  get schoolsLoading(): boolean {
    return this.schoolLinksData.isLoading;
  }

  get schoolsUnavailable(): boolean {
    return this.schoolLinksData.value === 'error';
  }

  get schoolRows(): SchoolRow[] {
    const value = this.schoolLinksData.value;
    return Array.isArray(value) ? value : [];
  }

  /** Always-visible head of the list; the rest sit behind the disclosure. */
  get visibleSchoolRows(): SchoolRow[] {
    return this.schoolRows.slice(0, SCHOOL_ROWS_ALWAYS_VISIBLE);
  }

  get overflowSchoolRows(): SchoolRow[] {
    return this.schoolRows.slice(SCHOOL_ROWS_ALWAYS_VISIBLE);
  }

  /** Per-relationship age limits; a blank entry means "no limit" and is skipped. */
  get ageLimitRows() {
    const raw = getFieldValue(this.args.fields, 'DEPENDENT_PROGRAM_AGE_LIMITS');
    const limits = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    return Object.entries(limits)
      .map(([relationship, age]) => ({ relationship, age: String(age ?? '').trim() }))
      .filter((entry) => entry.age)
      .sort((a, b) => a.relationship.localeCompare(b.relationship))
      .map((entry) => ({
        label: humanizeEnumName(entry.relationship),
        value: this.intl.t('program_builder.summary.value.age_years', {
          count: Number(entry.age),
        }),
      }));
  }

  <template>
    <div class="space-y-5" ...attributes>
      <SectionPanel
        @icon={{PhUserCircle}}
        @title={{t "program_builder.readout.eligibility_title"}}
        @iconColor="violet"
      >
        <:body>
          <div class="tio-row-between gap-3 text-sm" data-test-readout-waiting-period>
            <span class="tio-fg-muted">
              {{t "program_builder.readout.waiting_period"}}
              {{#if @showFieldNames}}
                <code
                  class="ml-1 font-mono tio-text-micro tio-fg-brand"
                >ELIGIBILITY_WAITING_PERIOD</code>
              {{/if}}
            </span>
            <span
              class="text-right font-medium {{if (eq this.waitingPeriodValue '—') 'tio-fg-faint'}}"
            >{{this.waitingPeriodValue}}</span>
          </div>
        </:body>
      </SectionPanel>

      <SectionPanel
        @icon={{PhBuildings}}
        @title={{t "program_builder.readout.institutions_title"}}
        @iconColor="violet"
      >
        <:body>
          <div class="space-y-1">
            <div class="tio-row-between gap-3 py-0.5 text-sm" data-test-readout-unaccredited>
              <span class="tio-fg-muted">
                {{t "program_builder.readout.allow_unaccredited"}}
                {{#if @showFieldNames}}
                  <code
                    class="ml-1 font-mono tio-text-micro tio-fg-brand"
                  >ALLOW_UNACCREDITED_INSTITUTIONS</code>
                {{/if}}
              </span>
              <span class="text-right font-medium">{{this.allowUnaccreditedValue}}</span>
            </div>
            <div class="tio-row-between gap-3 py-0.5 text-sm" data-test-readout-list-exclusive>
              <span class="tio-fg-muted">
                {{t "program_builder.readout.list_exclusive"}}
                {{#if @showFieldNames}}
                  <code
                    class="ml-1 font-mono tio-text-micro tio-fg-brand"
                  >INSTITUTION_LIST_IS_EXCLUSIVE</code>
                {{/if}}
              </span>
              <span class="text-right font-medium">{{this.listExclusiveValue}}</span>
            </div>
          </div>

          <div class="my-4 h-px tio-bg-sunken"></div>

          <div class="tio-row-tight">
            <PhGraduationCap @size={{16}} class="tio-fg-muted" aria-hidden="true" />
            <span class="tio-text-sm-medium">{{t "program_builder.readout.schools_heading"}}</span>
            {{#unless this.schoolsLoading}}
              {{#unless this.schoolsUnavailable}}
                <Chip @size="sm" @intent="primary" @appearance="faded">{{t
                    "program_builder.readout.schools_count"
                    count=this.schoolRows.length
                  }}</Chip>
              {{/unless}}
            {{/unless}}
          </div>
          <p class="mt-1 text-xs tio-fg-muted">{{t this.schoolsHintKey}}</p>

          {{#if this.schoolsLoading}}
            <p class="mt-2 text-sm tio-fg-muted">{{t "program_builder.readout.schools_loading"}}</p>
          {{else if this.schoolsUnavailable}}
            <p class="mt-2 text-sm tio-fg-muted" data-test-readout-schools-unavailable>{{t
                "program_builder.readout.schools_unavailable"
              }}</p>
          {{else if this.schoolRows.length}}
            {{! No inner scrollbox — it traps the wheel while paging the long
            read-out. The head of the list is always visible; the rest sit
            behind a native disclosure. }}
            <div class="mt-2 rounded-lg border tio-border-default" data-test-readout-schools-list>
              {{#each this.visibleSchoolRows as |row|}}
                <SchoolRowItem @row={{row}} />
              {{/each}}
              {{#if this.overflowSchoolRows.length}}
                <details>
                  <summary
                    class="cursor-pointer px-3 py-2 tio-text-xs-medium tio-fg-brand"
                    data-test-readout-schools-toggle
                  >
                    {{t "program_builder.readout.schools_show_all" count=this.schoolRows.length}}
                  </summary>
                  {{#each this.overflowSchoolRows as |row|}}
                    <SchoolRowItem @row={{row}} />
                  {{/each}}
                </details>
              {{/if}}
            </div>
          {{else}}
            <p class="mt-2 text-sm tio-fg-muted" data-test-readout-schools-empty>{{t
                "program_builder.readout.schools_empty"
              }}</p>
          {{/if}}
        </:body>
      </SectionPanel>

      <SectionPanel
        @icon={{PhUsersThree}}
        @title={{t "program_builder.readout.dependents_title"}}
        @iconColor="violet"
      >
        <:body>
          {{#if this.dependentProgram}}
            <div class="tio-row-between gap-3 text-sm" data-test-readout-dependent-program>
              <span class="tio-fg-muted">
                {{t "program_builder.readout.dependent_program"}}
                {{#if @showFieldNames}}
                  <code class="ml-1 font-mono tio-text-micro tio-fg-brand">DEPENDENT_PROGRAM</code>
                {{/if}}
              </span>
              <span class="text-right font-medium">{{this.dependentProgramValue}}</span>
            </div>

            <div class="tio-text-overline mt-3 tio-fg-muted">
              {{t "program_builder.readout.age_limits_heading"}}
              {{#if @showFieldNames}}
                <code
                  class="ml-1 font-mono normal-case tio-text-micro tio-fg-brand"
                >DEPENDENT_PROGRAM_AGE_LIMITS</code>
              {{/if}}
            </div>
            {{#if this.ageLimitRows.length}}
              <div class="mt-1 space-y-1">
                {{#each this.ageLimitRows as |row|}}
                  <div
                    class="tio-row-between gap-3 py-0.5 text-sm"
                    data-test-readout-age-limit-row={{row.label}}
                  >
                    <span class="tio-fg-muted">{{row.label}}</span>
                    <span class="text-right font-medium">{{row.value}}</span>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <p class="mb-0 mt-1 text-sm tio-fg-faint" data-test-readout-no-age-limits>{{t
                  "program_builder.readout.no_age_limits"
                }}</p>
            {{/if}}
          {{else}}
            <p class="mb-0 text-sm tio-fg-muted" data-test-readout-not-dependent>
              {{t "program_builder.readout.not_dependent_program"}}
              {{#if @showFieldNames}}
                <code class="ml-1 font-mono tio-text-micro tio-fg-brand">DEPENDENT_PROGRAM</code>
              {{/if}}
            </p>
          {{/if}}
        </:body>
      </SectionPanel>
    </div>
  </template>
}
